import { useCallback, useEffect, useRef, useState } from 'react';
import { prompt } from 'webtonative/AppReview';
import {
	APP_REVIEW_PROMPT_DELAY_MS,
	getAppReviewPlatform,
	hasAppReviewCooldownElapsed,
	type AppReviewPlatform,
	type AppReviewTriggerSource,
} from '@/utils';
import { useClaimAppReviewPromptMutation } from '@/store/api';
import {
	PROBABILITY_MIXPANEL_EVENTS,
	trackProbabilityMixpanelEvent,
} from '@/modules/probability-event/utils/analytics.utils';

type AppReviewAnalyticsUser = {
	id?: string;
	email?: string | null;
	username?: string | null;
	app_review_prompt_date?: string | Date | null;
};

interface UseAppReviewPromptOptions {
	/** The page asking; the server-side claim and 90-day cooldown are shared across pages. */
	triggerSource: AppReviewTriggerSource;
	user?: AppReviewAnalyticsUser | null;
	eventId?: string | null;
	eventName?: string | null;
	isReady: boolean;
	delayMs?: number;
}

type PreflightResult =
	| {
			eligible: true;
			isMock: boolean;
			platform: AppReviewPlatform | 'web';
	  }
	| { eligible: false };

const getPreflightResult = (user: AppReviewAnalyticsUser | null | undefined): PreflightResult => {
	if (!user?.id || !hasAppReviewCooldownElapsed(user.app_review_prompt_date)) {
		return { eligible: false };
	}

	const nativePlatform = getAppReviewPlatform();
	const isMock = import.meta.env.VITE_ENVIRONMENT === 'dev';
	const platform = isMock ? 'web' : nativePlatform;

	if (!platform) {
		return { eligible: false };
	}

	return { eligible: true, isMock, platform };
};

export function useAppReviewPrompt({
	triggerSource,
	user,
	eventId,
	eventName,
	isReady,
	delayMs = APP_REVIEW_PROMPT_DELAY_MS,
}: UseAppReviewPromptOptions) {
	const isFinishedRef = useRef(false);
	const [claimAppReviewPrompt] = useClaimAppReviewPromptMutation();
	const [isMockPromptOpen, setIsMockPromptOpen] = useState(false);
	const closeMockPrompt = useCallback(() => setIsMockPromptOpen(false), []);
	const submitMockRating = useCallback(() => setIsMockPromptOpen(false), []);

	const trackRequested = useCallback(
		(extra: Record<string, unknown>) => {
			trackProbabilityMixpanelEvent(PROBABILITY_MIXPANEL_EVENTS.APP_REVIEW_PROMPT_REQUESTED, {
				...extra,
				eventId,
				eventName,
				user,
				trigger_source: triggerSource,
			});
		},
		[eventId, eventName, triggerSource, user]
	);

	useEffect(() => {
		if (isFinishedRef.current || !isReady || !user?.id) return;

		if (document.visibilityState !== 'visible') {
			isFinishedRef.current = true;
			return;
		}

		const initialPreflight = getPreflightResult(user);
		if (!initialPreflight.eligible) {
			isFinishedRef.current = true;
			return;
		}

		let cancelled = false;
		const timeoutId = window.setTimeout(async () => {
			if (cancelled || isFinishedRef.current) return;

			if (document.visibilityState !== 'visible') {
				isFinishedRef.current = true;
				return;
			}

			const finalPreflight = getPreflightResult(user);
			if (!finalPreflight.eligible) {
				isFinishedRef.current = true;
				return;
			}

			try {
				const claim = await claimAppReviewPrompt(undefined).unwrap();
				if (cancelled || isFinishedRef.current) return;

				isFinishedRef.current = true;
				if (!claim?.should_prompt) return;

				if (finalPreflight.isMock) {
					setIsMockPromptOpen(true);
				} else {
					prompt();
				}

				trackRequested({
					platform: finalPreflight.platform,
					is_mock: finalPreflight.isMock,
					attempt_timestamp: claim.app_review_prompt_date,
				});
			} catch {
				isFinishedRef.current = true;
			}
		}, delayMs);

		const cancelWhenHidden = () => {
			if (document.visibilityState === 'visible' || isFinishedRef.current) return;

			window.clearTimeout(timeoutId);
			isFinishedRef.current = true;
		};

		document.addEventListener('visibilitychange', cancelWhenHidden);

		return () => {
			cancelled = true;
			window.clearTimeout(timeoutId);
			document.removeEventListener('visibilitychange', cancelWhenHidden);
		};
	}, [claimAppReviewPrompt, delayMs, isReady, trackRequested, user]);

	return {
		isMockPromptOpen,
		dismissMockPrompt: closeMockPrompt,
		submitMockRating,
	};
}
